/**
 * Battement de coeur periodique et etat de la connexion du poste.
 *
 * - un battement au demarrage, puis toutes les `intervalMs` ;
 * - un battement tout de suite quand le navigateur annonce le retour du
 *   reseau (`online`), « hors ligne » tout de suite quand il annonce la perte
 *   (`offline`) ;
 * - `onResult` recoit chaque resultat (statut du poste, retour en ligne).
 */
import { useEffect, useRef, useState } from 'react';
import { CONNECTION, checkConnection } from './heartbeat';

export function useHeartbeat({ enabled, api, token, appVersion, intervalMs, onResult, getCatalogSnapshotId, win = globalThis.window }) {
    const [connection, setConnection] = useState(CONNECTION.CHECKING);
    const onResultRef = useRef(onResult);
    onResultRef.current = onResult;
    const snapshotRef = useRef(getCatalogSnapshotId);
    snapshotRef.current = getCatalogSnapshotId;

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        let timer = null;
        let running = false;
        let previous = CONNECTION.CHECKING;

        const schedule = () => {
            clearTimeout(timer);
            timer = setTimeout(beat, intervalMs);
        };

        async function beat() {
            if (cancelled || running) return;
            running = true;
            try {
                const catalogSnapshotId = snapshotRef.current ? snapshotRef.current() : null;
                const result = await checkConnection({ api, token, appVersion, catalogSnapshotId, nav: win && win.navigator });
                if (cancelled) return;
                setConnection(result.connection);
                const cameOnline = result.connection === CONNECTION.ONLINE && previous !== CONNECTION.ONLINE;
                previous = result.connection;
                if (onResultRef.current) onResultRef.current({ ...result, cameOnline });
            } finally {
                running = false;
                if (!cancelled) schedule();
            }
        }

        const handleOnline = () => beat();
        const handleOffline = () => {
            previous = CONNECTION.OFFLINE;
            setConnection(CONNECTION.OFFLINE);
        };

        if (win) {
            win.addEventListener('online', handleOnline);
            win.addEventListener('offline', handleOffline);
        }
        beat();

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (win) {
                win.removeEventListener('online', handleOnline);
                win.removeEventListener('offline', handleOffline);
            }
        };
    }, [enabled, api, token, appVersion, intervalMs, win]);

    return connection;
}
